import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { OnboardingStep } from './OnboardingStep';
import { OnboardingStepProps } from './OnboardingStep';

export const CustomizationStep: React.FC<OnboardingStepProps> = ({
  data,
  onNext,
  onPrevious,
}) => {
  const { t } = useLanguage();
  const [customizations, setCustomizations] = useState({
    roleCustomizations: data.customizations?.roleCustomizations || {},
    userCustomizations: data.customizations?.userCustomizations || {},
    hierarchyCustomizations: data.customizations?.hierarchyCustomizations || {},
  });

  const handleCustomizationChange = (type: 'role' | 'user' | 'hierarchy', key: string, value: any) => {
    setCustomizations(prev => ({
      ...prev,
      [`${type}Customizations`]: {
        ...prev[`${type}Customizations`],
        [key]: value
      }
    }));
  };

  const handleNext = () => {
    onNext({
      customizations: {
        roleCustomizations: customizations.roleCustomizations,
        userCustomizations: customizations.userCustomizations,
        hierarchyCustomizations: customizations.hierarchyCustomizations,
      }
    });
  };

  const canGoNext = true; // Customization is optional

  return (
    <OnboardingStep
      title={t('onboarding.customization.title')}
      description={t('onboarding.customization.description')}
      onNext={handleNext}
      onPrevious={onPrevious}
      canGoNext={canGoNext}
      showNext={true}
      showPrevious={true}
    >
      <div className="space-y-8">
        {/* Role Customizations */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.customization.roles.title')}
          </h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.roles.defaultPermissions')}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => handleCustomizationChange('role', 'defaultPermissions', e.target.value)}
                >
                  <option value="minimal">{t('onboarding.customization.roles.minimal')}</option>
                  <option value="standard">{t('onboarding.customization.roles.standard')}</option>
                  <option value="extended">{t('onboarding.customization.roles.extended')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.roles.roleInheritance')}
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="roleInheritance"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    onChange={(e) => handleCustomizationChange('role', 'inheritance', e.target.checked)}
                  />
                  <label htmlFor="roleInheritance" className="ml-2 block text-sm text-gray-900">
                    {t('onboarding.customization.roles.enableInheritance')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Customizations */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.customization.users.title')}
          </h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.users.defaultProfile')}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => handleCustomizationChange('user', 'defaultProfile', e.target.value)}
                >
                  <option value="basic">{t('onboarding.customization.users.basic')}</option>
                  <option value="detailed">{t('onboarding.customization.users.detailed')}</option>
                  <option value="comprehensive">{t('onboarding.customization.users.comprehensive')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.users.autoAssignment')}
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoAssignment"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    onChange={(e) => handleCustomizationChange('user', 'autoAssignment', e.target.checked)}
                  />
                  <label htmlFor="autoAssignment" className="ml-2 block text-sm text-gray-900">
                    {t('onboarding.customization.users.enableAutoAssignment')}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hierarchy Customizations */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.customization.hierarchies.title')}
          </h3>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.hierarchies.defaultStructure')}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => handleCustomizationChange('hierarchy', 'defaultStructure', e.target.value)}
                >
                  <option value="flat">{t('onboarding.customization.hierarchies.flat')}</option>
                  <option value="hierarchical">{t('onboarding.customization.hierarchies.hierarchical')}</option>
                  <option value="matrix">{t('onboarding.customization.hierarchies.matrix')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onboarding.customization.hierarchies.maxLevels')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5"
                  onChange={(e) => handleCustomizationChange('hierarchy', 'maxLevels', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Optional Advanced Settings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('onboarding.customization.advanced.title')}
          </h3>
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableAuditLogging"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  onChange={(e) => handleCustomizationChange('role', 'auditLogging', e.target.checked)}
                />
                <label htmlFor="enableAuditLogging" className="ml-2 block text-sm text-gray-900">
                  {t('onboarding.customization.advanced.auditLogging')}
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableNotifications"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  onChange={(e) => handleCustomizationChange('user', 'notifications', e.target.checked)}
                />
                <label htmlFor="enableNotifications" className="ml-2 block text-sm text-gray-900">
                  {t('onboarding.customization.advanced.notifications')}
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableIntegration"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  onChange={(e) => handleCustomizationChange('hierarchy', 'integration', e.target.checked)}
                />
                <label htmlFor="enableIntegration" className="ml-2 block text-sm text-gray-900">
                  {t('onboarding.customization.advanced.integration')}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </OnboardingStep>
  );
};
